/*
 * cb-layout-shift.js v1.0 - field layout-shift (CLS) attribution for product pages (28 Sep 2026)
 *
 * Why: Clarity shows product-page layout shift of 0.2-0.8 on real phones (worst on slow Instagram /
 * Facebook in-app loads) but not WHICH element moves. This records every unexpected shift the
 * browser reports (Layout Instability API - Chrome / Android WebView / Edge; Safari has no API
 * and is skipped) and names the element that moved, so fixes target the real cause.
 *
 * What is counted: only shifts the browser itself counts toward CLS (hadRecentInput = false).
 * Shifts right after a tap are excluded by the browser, exactly as in Core Web Vitals.
 *
 * Events (Clarity event + session tags, and GA4 - same params as cb-gallery-guard):
 *   cb_layout_shift   one per shift >= MIN_REPORT (max MAX_EVENTS per page), biggest first moved
 *                     element. cb_detail = "<value> <phase> <element> dy=<px> dx=<px> y=<px> sy=<px>"
 *                       phase:   pre_init = before the page's scripts finished (before
 *                                DOMContentLoaded end - theme.js / pf-bundle.js not run yet)
 *                                post_init+<ms> = that long after they finished
 *                       element: tag#id.class.class, prefixed with its nearest product block
 *                                or section so it can be found in the theme
 *                       dy/dx:   how far it moved (px, + = down/right); y = its top before the
 *                                move (px from top of screen); sy = page scroll position
 *                     cb_ms = ms since navigation start
 *   cb_cls_summary    once per page, on hide: CLS as Google computes it (largest 5s session window),
 *                     number of shifts, and the element behind the largest shift.
 *
 * Speed budget: no work until the browser reports a shift; one PerformanceObserver; reads only
 * (no layout writes, no style reads); nothing sent when the page did not shift.
 */
(function () {
  'use strict';
  var w = window, d = document;
  if (w.__cbLayoutShift || !/\/products\//.test(location.pathname)) return;
  if (!('PerformanceObserver' in w) || !PerformanceObserver.supportedEntryTypes ||
      PerformanceObserver.supportedEntryTypes.indexOf('layout-shift') < 0) return;
  w.__cbLayoutShift = true;

  var MIN_REPORT = 0.01;   // ignore tiny shifts
  var MAX_EVENTS = 5;      // per page, per event name
  // TRACKING-FIX (13 Oct 2026, Claude): GA4 G-80LLZ17V1J only (was also G-8899R3KRW0, a 2nd
  // property that got only these cb_* events - same data twice, and an extra Google script).
  // Undo: add 'G-8899R3KRW0' back to this list.
  var GA4_IDS = ['G-80LLZ17V1J'];

  w.clarity = w.clarity || function () { (w.clarity.q = w.clarity.q || []).push(arguments); };
  var ua = navigator.userAgent || '';
  var browserType = /Instagram/i.test(ua) ? 'instagram_app'
    : /FBAN|FBAV|FB_IAB/i.test(ua) ? 'facebook_app'
    : /\bwv\b|WebView/i.test(ua) ? 'android_webview' : 'browser';

  var sent = 0;
  function report(name, detail, ms, value) {
    try {
      if (name === 'cb_layout_shift' && ++sent > MAX_EVENTS) return;
      detail = String(detail).slice(0, 250);
      w.clarity('event', name);
      w.clarity('set', name, detail);
      w.clarity('set', 'cb_browser', browserType);
      if (typeof w.gtag === 'function') {
        w.gtag('event', name, {
          send_to: GA4_IDS,
          transport_type: 'beacon',
          cb_detail: detail,
          cb_ms: ms,
          cb_value: value,
          cb_browser: browserType,
          cb_page: location.pathname.slice(0, 100)
        });
      }
    } catch (e) { /* never break the page */ }
  }

  // ---- naming the element that moved ----
  function short(el) {
    if (!el || el.nodeType !== 1) return '';
    var s = el.tagName.toLowerCase();
    if (el.id) s += '#' + el.id.slice(0, 30);
    var cls = typeof el.className === 'string' ? el.className.trim().split(/\s+/) : [];
    for (var i = 0, n = 0; i < cls.length && n < 2; i++) {
      if (cls[i] && !/^(js|aos-animate|is-|flickity)/.test(cls[i])) { s += '.' + cls[i].slice(0, 30); n++; }
    }
    return s;
  }
  function describe(node) {
    var el = node && node.nodeType === 1 ? node : node && node.parentElement;
    if (!el) return 'unknown';
    var own = short(el);
    var ctx = el.parentElement && el.parentElement.closest(
      '.product-block, [data-product-images], .variant-wrapper, pf-bundle-selector, .pf-bundle-selector, .shopify-section, header, footer'
    );
    var ctxName = ctx ? short(ctx) : '';
    return ctxName && ctxName !== own ? ctxName + '>' + own : own;
  }

  // ---- when the page's own scripts had finished (theme.js, pf-bundle.js etc. are deferred) ----
  function initDoneAt() {
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      if (nav && nav.domContentLoadedEventEnd > 0) return nav.domContentLoadedEventEnd;
    } catch (e) {}
    return 0;
  }

  // ---- CLS the way Google computes it: largest session window (gap < 1s, window <= 5s) ----
  var winValue = 0, winFirst = 0, winLast = 0, cls = 0, shifts = 0;
  var biggest = { value: 0, what: '' };
  var pending = []; // shifts seen before DOMContentLoaded finished; phase decided later

  function phaseOf(t) {
    var done = initDoneAt();
    if (!done || t < done) return 'pre_init';
    return 'post_init+' + Math.round(t - done);
  }

  function handle(entry) {
    if (entry.hadRecentInput) return;
    var v = entry.value;
    shifts++;
    if (winValue && entry.startTime - winLast < 1000 && entry.startTime - winFirst < 5000) {
      winValue += v;
    } else {
      winValue = v;
      winFirst = entry.startTime;
    }
    winLast = entry.startTime;
    if (winValue > cls) cls = winValue;

    // The source that moved furthest is the one to name.
    var best = null, bestDist = -1;
    (entry.sources || []).forEach(function (s) {
      if (!s.previousRect || !s.currentRect) return;
      var dist = Math.abs(s.currentRect.y - s.previousRect.y) + Math.abs(s.currentRect.x - s.previousRect.x);
      if (dist > bestDist) { bestDist = dist; best = s; }
    });
    var what = best ? describe(best.node) : 'unknown';
    if (v > biggest.value) biggest = { value: v, what: what };
    if (v < MIN_REPORT) return;

    var dy = best ? Math.round(best.currentRect.y - best.previousRect.y) : 0;
    var dx = best ? Math.round(best.currentRect.x - best.previousRect.x) : 0;
    var y = best ? Math.round(best.previousRect.y) : 0;
    var rec = { t: entry.startTime, v: v, what: what, dy: dy, dx: dx, y: y, sy: Math.round(w.scrollY || 0) };
    if (initDoneAt()) send(rec); else pending.push(rec);
  }

  function send(r) {
    report('cb_layout_shift',
      r.v.toFixed(3) + ' ' + phaseOf(r.t) + ' ' + r.what + ' dy=' + r.dy + ' dx=' + r.dx + ' y=' + r.y + ' sy=' + r.sy,
      Math.round(r.t), Math.round(r.v * 1000));
  }
  function flushPending() {
    if (!pending.length) return;
    var list = pending; pending = [];
    list.sort(function (a, b) { return b.v - a.v; }).forEach(send);
  }

  var po;
  try {
    po = new PerformanceObserver(function (list) { list.getEntries().forEach(handle); });
    // buffered: also delivers the shifts that happened before this deferred script ran
    po.observe({ type: 'layout-shift', buffered: true });
  } catch (e) { return; }

  // Deferred scripts run just BEFORE DOMContentLoaded, so wait for it unless it already finished.
  if (initDoneAt()) {
    setTimeout(flushPending, 0);
  } else {
    d.addEventListener('DOMContentLoaded', function () { setTimeout(flushPending, 0); });
  }

  var summarySent = false;
  function onHide() {
    try { if (po && po.takeRecords) po.takeRecords().forEach(handle); } catch (e) {}
    flushPending();
    if (summarySent || !shifts) return;
    summarySent = true;
    report('cb_cls_summary',
      'cls=' + cls.toFixed(3) + ' shifts=' + shifts + ' top=' + biggest.value.toFixed(3) + ' ' + biggest.what,
      Math.round(performance.now()), Math.round(cls * 1000));
    try { w.clarity('set', 'cb_cls', cls >= 0.25 ? 'poor' : cls >= 0.1 ? 'needs_improvement' : 'good'); } catch (e) {}
  }
  d.addEventListener('visibilitychange', function () { if (d.visibilityState === 'hidden') onHide(); });
  w.addEventListener('pagehide', onHide);
})();
